import {Link, useSearchParams} from '@remix-run/react';

export interface PublicationsPaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
}

function getPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({length: total}, (_, i) => i + 1);

  const pages: (number | 'ellipsis')[] = [1];

  if (current > 3) pages.push('ellipsis');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);

  if (current < total - 2) pages.push('ellipsis');

  pages.push(total);
  return pages;
}

function buildPageUrl(searchParams: URLSearchParams, page: number): string {
  const params = new URLSearchParams(searchParams);
  if (page <= 1) {
    params.delete('page');
  } else {
    params.set('page', String(page));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '.';
}

export function PublicationsPagination({
  currentPage,
  totalPages,
  total,
}: PublicationsPaginationProps) {
  const [searchParams] = useSearchParams();

  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <nav className="flex items-center justify-between mt-6" aria-label="Paginação">
      <span className="text-sm text-shade-11">
        {total} {total === 1 ? 'publicação' : 'publicações'}
      </span>
      <div className="flex items-center gap-1">
        {currentPage > 1 && (
          <Link
            to={buildPageUrl(searchParams, currentPage - 1)}
            className="px-2 py-1 rounded text-sm text-shade-11 hover:bg-shade-3"
            aria-label="Página anterior"
          >
            ←
          </Link>
        )}
        {pages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-shade-9">
                …
              </span>
            );
          }

          const isActive = page === currentPage;
          return (
            <Link
              key={page}
              to={buildPageUrl(searchParams, page)}
              className={`px-2.5 py-1 rounded text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-9 text-white'
                  : 'text-shade-11 hover:bg-shade-3'
              }`}
              aria-current={isActive ? 'page' : undefined}
              aria-label={`Página ${page}`}
            >
              {page}
            </Link>
          );
        })}
        {currentPage < totalPages && (
          <Link
            to={buildPageUrl(searchParams, currentPage + 1)}
            className="px-2 py-1 rounded text-sm text-shade-11 hover:bg-shade-3"
            aria-label="Próxima página"
          >
            →
          </Link>
        )}
      </div>
    </nav>
  );
}
